import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { PageSection } from '../../../components/layout/PageSection';
import { Input } from '../../../components/ui/FormField';
import { usePageTitle } from '../../../lib/hooks.js';
import { DataView } from "../../../components/ui/DataView";
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { useAuthLogs, type AuthLogEntry } from '../../../features/reports/client';

export const Route = createFileRoute('/_auth/logs/auth')({
  component: AuthLogPage,
});

function formatEventType(raw: string): string {
  return raw.replace(/^auth\./, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isFailure(eventType: string): boolean {
  return eventType.endsWith('_failure') || eventType.includes('failure');
}

function isSuccess(eventType: string): boolean {
  return eventType.endsWith('_success') || eventType.includes('success');
}

function AuthLogPage() {
  usePageTitle('Authentication Logs');
  const [statusFilter, setStatusFilter] = useState('');
  const [date, setDate] = useState('');

  const filters: Record<string, string> = {};
  if (statusFilter) filters.status = statusFilter;
  if (date) {
    filters.from = date;
    const to = new Date(date);
    to.setDate(to.getDate() + 1);
    filters.to = to.toISOString().split('T')[0];
  }

  const authlogsQuery = useAuthLogs(Object.keys(filters).length > 0 ? filters : undefined);
  const { data: response } = authlogsQuery;
  const dataViewQuery = { ...authlogsQuery, data: response?.data };

  return (
    <div>
      <PageHeader title="Authentication Logs" className="mb-6" />
      <div className="mb-6 flex gap-4 items-center">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary h-9 px-3"
        >
          <option value="">All Events</option>
          <option value="success">Successful</option>
          <option value="failure">Failed</option>
        </select>
        <Input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-auto"
        />
      </div>

      <DataView query={dataViewQuery}>
        {(data) => (
          <PageSection>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border-border text-left whitespace-nowrap">
                <thead className="bg-surface-2">
                  <tr>
                    <th className="border px-4 py-2">Timestamp</th>
                    <th className="border px-4 py-2">Event</th>
                    <th className="border px-4 py-2">Status</th>
                    <th className="border px-4 py-2">User ID</th>
                    <th className="border px-4 py-2">Identifier</th>
                    <th className="border px-4 py-2">IP Address</th>
                    <th className="border px-4 py-2">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {(data as AuthLogEntry[]).map((log) => (
                    <tr key={log.id} className={isFailure(log.eventType) ? 'bg-danger-bg' : ''}>
                      <td className="border px-4 py-2 font-mono text-xs">
                        {log.occurredAt ? new Date(log.occurredAt).toLocaleString() : '—'}
                      </td>
                      <td className="border px-4 py-2 text-sm">{formatEventType(log.eventType)}</td>
                      <td className="border px-4 py-2">
                        {isFailure(log.eventType) ? (
                          <StatusBadge status="danger" label="Failed" />
                        ) : isSuccess(log.eventType) ? (
                          <StatusBadge status="success" label="Success" />
                        ) : (
                          <span className="text-xs text-text-muted">{formatEventType(log.eventType)}</span>
                        )}
                      </td>
                      <td className="border px-4 py-2 font-mono text-xs">{log.actorId || '—'}</td>
                      <td className="border px-4 py-2 text-sm font-medium">
                        {(log.payload?.identifier as string) || '—'}
                      </td>
                      <td className="border px-4 py-2 font-mono text-xs">{log.ipAddress || '—'}</td>
                      <td className="border px-4 py-2 text-xs text-text-muted max-w-[200px] truncate" title={log.userAgent ?? ''}>
                        {log.userAgent || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PageSection>
        )}
      </DataView>
    </div>
  );
}